import {
  Button,
  Container,
  Stack,
  Typography,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from 'src/contexts/AppContext';
import { QrCode2 } from '@mui/icons-material';
import QrCodeDialog from '@/components/settings/QrCodeDialog';
import { useActivePersona } from '@/personas/useActivePersona';
import useRole from '@/personas/useRole';
import { useCharacter } from '@/contexts/CharacterContext';
import { characters } from '@/components/character';
import { setNextUpEnabled, setTransitionWarningEnabled } from '@/utils/npfNudges';
import { useNpfNudgePrefs } from '@/components/child';
import SettingsAccordion from 'src/components/settings/SettingsAccordion';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';

/**
 * Gemensam SettingsPage för ALLA personas. Visar:
 * - Child/Teen: Kompis-inställningar, NPF-stöd
 * - Adult/Young: QR-kod
 * - Alla: SettingsAccordion (dark mode, tema, roll, notis, vyläge längst ner)
 *
 * Brukarvyn har INGEN väg till handledarläget. Barn och förälder har separata
 * konton, så det finns inget läge att växla till på brukarens konto — raden
 * "Gå till föräldraläge" och dess bekräftelsedialog är därför borttagna. Vägen
 * TILLBAKA från handledarläget (supervisor.exit.*) står kvar: den går åt andra
 * hållet, på handledarens eget konto, och behövs för att en handledare ska nå
 * sin egen dag.
 */

const Toggle = ({ on, onToggle, label }: { on: boolean; onToggle: () => void; label: string }) => (
  <Box
    component="button"
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    onClick={onToggle}
    sx={{
      width: 44,
      height: 24,
      borderRadius: '12px',
      bgcolor: on ? 'success.main' : 'grey.300',
      position: 'relative',
      cursor: 'pointer',
      border: 'none',
      transition: 'background 0.2s',
      flexShrink: 0,
      '&::after': {
        content: '""',
        position: 'absolute',
        width: 18,
        height: 18,
        borderRadius: '50%',
        bgcolor: 'primary.contrastText',
        top: 3,
        left: on ? 23 : 3,
        transition: 'left 0.2s',
        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
      },
    }}
  />
);

/**
 * En rad i inställningarna. Raden är en KNAPP när (och bara när) den har onClick.
 *
 * Tillgänglighet: en `<Box onClick>` utan role/tabIndex/onKeyDown går inte att nå
 * med tangentbord och annonseras inte som interaktiv av skärmläsare. Det gjorde
 * vägen ut ur handledarläget — enda produktionsvägen — otillgänglig. Attributen
 * sätts därför här, en gång, i stället för per anropsplats.
 *
 * Rader UTAN onClick (de som bara innehåller en Toggle eller ett TextField) får
 * INGET av detta: de ska inte bli fokuserbara knappar, och deras interaktiva barn
 * har redan egen fokus och egen tangentbordshantering. Därför är hela paketet
 * villkorat på onClick, inte alltid påslaget.
 */
const SettingRow = ({
  children,
  onClick,
  tk: tokens,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  tk: { card: string; border: string };
}) => {
  const clickableProps = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          // Mellanslag scrollar sidan som standard, Enter kan skicka formulär.
          event.preventDefault();
          onClick();
        },
      }
    : {};

  return (
    <Box
      onClick={onClick}
      {...clickableProps}
      sx={{
        bgcolor: tokens.card,
        border: `1px solid ${tokens.border}`,
        borderRadius: '12px',
        p: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: onClick ? 'pointer' : 'default',
        // Syns bara på rader som faktiskt kan få fokus (de med onClick ovan).
        '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: '2px' },
      }}
    >
      {children}
    </Box>
  );
};

const SettingsPage = () => {
  const { t } = useTranslation();
  const { user } = useContext(AppContext);
  const profile = useActivePersona();
  const persona = profile.persona;
  const navigate = useNavigate();
  const { role, setRole } = useRole();
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);

  const [qrCodeDialog, setQrCodeDialog] = useState(false);
  const [exitToUserConfirmOpen, setExitToUserConfirmOpen] = useState(false);

  // Child/teen companion
  const { selectedCharacter, characterName, chooseCharacter } = useCharacter();
  const [nameDraft, setNameDraft] = useState(characterName);
  const characterLabel = characters.find((c) => c.name === selectedCharacter)?.label ?? '';
  const { nextUpEnabled, transitionWarningEnabled } = useNpfNudgePrefs();

  const isChildOrTeen = persona === 'child' || persona === 'teen';
  // Handledarläget känns igen på ROLLEN, inte på isSupervising: rollen
  // (app:role:<tag>) är valet, isSupervising är mekaniken det driver. Att läsa
  // rollen här inför ingen andra sanning — SupervisorContext äger fortfarande
  // isSupervising/currentClient, och den här raden ändrar dem bara via setRole.
  const isSupervisorRole = role === 'supervisor';

  const saveName = () => {
    if (selectedCharacter) {
      chooseCharacter(selectedCharacter, nameDraft);
    }
  };

  // Handledarens väg TILLBAKA till sin egen dag. Den går på HANDLEDARENS konto och
  // ska inte förväxlas med den borttagna "Gå till föräldraläge"-raden, som gick åt
  // andra hållet från BRUKARENS konto (se docblocken överst).
  //
  // Utan den här raden har en handledare med minst en kopplad brukare ingen synlig
  // väg tillbaka: RoleSwitchSetting returnerar tom fragment i produktion, och
  // SelectClientPage:s tom-state renderas bara när clients.length === 0.
  //
  // setRole('user') driver setSupervising(false), som går via setClient(undefined)
  // (SupervisorContext.tsx:53 → :141) och DÄR redan dispatchar 'storage'. Den
  // manuella dispatchen nedan är alltså ett andra, identiskt varv — den är INTE
  // nödvändig för korrektheten, och det är medvetet: den står kvar som ett
  // redundant skyddsnät.
  //
  // Skälet är timing, inte täckning. Rollsynken är inte synkron: RoleGate stämmer
  // av isSupervising mot rollvalet i en useEffect (RoleGate.tsx:31), alltså ett
  // renderingsvarv senare. De useEffectiveChildMode/useActivePersona-instanser som
  // saknar SupervisorContext i scope (App.tsx-temat, Navbar, CharacterCompanion)
  // håller sina källor i useState och läser om enbart vid 'storage'. Att ta bort
  // raden för att spara ett varv skulle därför riskera en kort stund med
  // handledarläget kvar ovanpå brukarvyn — ett dubblerat event kostar ingenting,
  // en fönsterruta med fel tema gör det.
  const confirmExitToUser = () => {
    setExitToUserConfirmOpen(false);
    setRole('user');
    window.dispatchEvent(new Event('storage'));
    navigate('/');
  };

  const sectionTitle = (text: string) => (
    <Typography
      sx={{
        fontSize: '0.8rem',
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: tk.textSoft,
        mb: 1,
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Container sx={{ pb: 12 }}>
      <Stack spacing={3} pt={1.5}>
        <Typography
          sx={{ fontWeight: 900, fontSize: '1.5rem', color: tk.text, textAlign: 'center' }}
        >
          {t('Settings')}
        </Typography>

        {/* Adult/Young: QR-kod med tag */}
        {!isChildOrTeen && user && (
          <Stack direction="row" justifyContent="center">
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setQrCodeDialog(true)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Typography variant="body1" sx={{ marginRight: 1 }}>
                {`${user.firstName} (${user.tag})`}
              </Typography>
              <QrCode2 />
            </Button>
            <QrCodeDialog open={qrCodeDialog} onClose={() => setQrCodeDialog(false)} />
          </Stack>
        )}

        {/* Child/Teen: Kompis-inställningar */}
        {persona === 'child' && (
          <Box>
            {sectionTitle(t('child.settings.companionSection', 'Din kompis'))}
            <Stack spacing={1}>
              <SettingRow tk={tk}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                    {t('child.settings.companionName', 'Kompisens namn')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: tk.textSoft, fontWeight: 600 }}>
                    {characterName} {characterLabel && `· ${characterLabel}`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    size="small"
                    inputProps={{
                      maxLength: 16,
                      'aria-label': t('child.settings.companionName') as string,
                    }}
                    sx={{ width: 130 }}
                  />
                  <Box
                    component="button"
                    type="button"
                    onClick={saveName}
                    sx={{
                      px: 1.75,
                      py: 1,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: 800,
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {t('child.settings.save', 'Spara')}
                  </Box>
                </Box>
              </SettingRow>
              <SettingRow tk={tk} onClick={() => navigate('/onboarding')}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                    {t('child.settings.changeCompanion', 'Byt kompis')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: tk.textSoft, fontWeight: 600 }}>
                    {t('child.settings.changeCompanionHint', 'Välj en ny figur')}
                  </Typography>
                </Box>
                <span aria-hidden style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                  ›
                </span>
              </SettingRow>
            </Stack>
          </Box>
        )}

        {/* Child/Teen: NPF-stöd */}
        {isChildOrTeen && (
          <Box>
            {sectionTitle(t('child.settings.supportSection', 'Stöd'))}
            <Stack spacing={1}>
              <SettingRow tk={tk}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                  {t('child.settings.nextUpLabel', 'Nästa upp')}
                </Typography>
                <Toggle
                  on={nextUpEnabled}
                  onToggle={() => setNextUpEnabled(!nextUpEnabled)}
                  label={t('child.settings.nextUpLabel') as string}
                />
              </SettingRow>
              <SettingRow tk={tk}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                  {t('child.settings.transitionLabel', 'Övergångsvarning')}
                </Typography>
                <Toggle
                  on={transitionWarningEnabled}
                  onToggle={() => setTransitionWarningEnabled(!transitionWarningEnabled)}
                  label={t('child.settings.transitionLabel') as string}
                />
              </SettingRow>
            </Stack>
          </Box>
        )}

        {/* Övriga inställningar (accordion med dark mode, tema, roll, notis, vyläge längst ner) */}
        <SettingsAccordion />

        {/* Handledarläge: väg tillbaka till brukarläget (symmetriskt rollbyte).
            Synlig i PRODUKTION — till skillnad från RoleSwitchSetting, som är
            dev-only och dessutom bara öppnar rollväljaren igen. */}
        {isSupervisorRole && (
          <Box>
            {sectionTitle(t('supervisor.exit.section', 'Läge'))}
            <SettingRow tk={tk} onClick={() => setExitToUserConfirmOpen(true)}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                  {t('supervisor.exit.action', 'Gå till mitt eget läge')}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: tk.textSoft, fontWeight: 600 }}>
                  {t('supervisor.exit.hint', 'Lämnar handledarvyn')}
                </Typography>
              </Box>
              <span aria-hidden style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                ›
              </span>
            </SettingRow>
          </Box>
        )}
      </Stack>

      {/* Bekräftelse-dialog för att lämna handledarläget */}
      <Dialog
        open={exitToUserConfirmOpen}
        onClose={() => setExitToUserConfirmOpen(false)}
        aria-labelledby="exit-to-user-confirm-title"
      >
        <DialogTitle id="exit-to-user-confirm-title">
          {t('supervisor.exit.confirmTitle', 'Lämna handledarläget?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('supervisor.exit.confirmBody', 'Du kommer till ditt eget läge.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExitToUserConfirmOpen(false)}>
            {t('supervisor.exit.confirmCancel', 'Avbryt')}
          </Button>
          <Button onClick={confirmExitToUser} variant="contained" color="primary">
            {t('supervisor.exit.confirmConfirm', 'Ja, byt')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SettingsPage;
